import { useApolloClient } from "@apollo/client";
import { AgentDialogFormValues } from "../../features/agent/agentDialogFormValues.js";
import {
  CollapseSection,
  ErrorMessage,
  ModalContent,
  ModalDialog,
  ModalOpenProcess,
  ModalOpenProcessRef,
  ModalTitle,
} from "@msys/ui";
import { Add as AddIcon } from "@mui/icons-material";
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  DialogActions,
  FormControlLabel,
  Grid,
  IconButton,
  TextField as MuiTextField,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import {
  Formik,
  FormikErrors,
  FormikProps,
  FormikTouched,
  setNestedObjectValues,
} from "formik";
import { Moment } from "moment";
import React from "react";
import * as Yup from "yup";
import { RestrictedByCapabilityWithDebug } from "../../auth/RestrictedByCapability.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../auth/RestrictedByOrganisationPermission.js";
import { useUserData } from "../../auth/useUserData.js";
import {
  AddressInput,
  DesiredProjectInitialStatus,
  Money,
  ProjectType,
} from "../../../clients/graphqlTypes.js";
import { CheckboxField } from "../../commons/form-fields/CheckboxField.js";
import { DatePickerField } from "../../commons/form-fields/DatePickerField.js";
import { FormattedMoneyField } from "../../commons/form-fields/FormattedMoneyField.js";
import { SelectField } from "../../commons/form-fields/SelectField.js";
import { TextField } from "../../commons/form-fields/TextField.js";
import { AddressField } from "../addresses/AddressField.js";
import { getAddressLabel } from "../addresses/helpers.js";
import { CategoriesInput } from "../categories/CategoriesInput.js";
import { useCategories } from "../categories/useCategories.js";
import {
  CrmCompanySelect,
  CrmCompanySelectEntry,
} from "../crm-companies/CrmCompanySelect.js";
import { CrmCreateModal } from "../crm/CrmCreateModal.js";
import { CustomFieldConfigFragment } from "../custom-fields/customFieldConfigs.generated.js";
import {
  CustomFieldsArray,
  CustomFieldsFormValues,
  getCustomFieldsInitialValues,
  getCustomFieldsSubmitValues,
  getCustomFieldsValidationSchema,
} from "../custom-fields/EditCustomFieldModal.js";
import { useCustomFieldConfig } from "../custom-fields/useCustomFieldConfig.js";
import { createRecursiveTryFocusElementFunction } from "../../utils.js";
import { useProjectTypes } from "../projects/useProjectTypes.js";
import { resolveCreationTeamId } from "../teams/resolveCreationTeam.js";
import { ClientCard } from "./ClientCard.js";
import { ClientResolutionHint } from "./ClientResolutionHint.js";
import {
  deriveAddressOrigin,
  deriveBooleanOrigin,
  deriveClientOrigin,
  deriveCustomFieldsOrigin,
  deriveDateOrigin,
  deriveFieldOrigin,
  deriveIdListOrigin,
  deriveMoneyOrigin,
  FieldOrigin,
} from "./fieldOrigin.js";
import { FieldOriginHint } from "./FieldOriginHint.js";
import {
  mapPendingDocShareProjectPrefillToDraft,
  contactCandidateDisplayName,
  newContactToCrmCreateFormPrefill,
  PendingDocShareProjectDraft,
  PendingDocShareProjectDraftClient,
  recombineDraftCategories,
  splitDraftCategories,
} from "./projectDraft.js";
import {
  PendingDocShareProjectPrefill_ResultFragment,
  usePendingDocShareProjectPrefillQuery,
} from "./PendingDocShareProjectPrefill.generated.js";
import { AcceptPendingDocSharePartialFailureModal } from "./AcceptPendingDocSharePartialFailureModal.js";
import { SharedQuoteSummaryCard } from "./SharedQuoteSummaryCard.js";
import {
  UnfinishedPendingDocShareAcceptance,
  useAcceptPendingDocShareOrchestrator,
} from "./useAcceptPendingDocShareOrchestrator.js";
import { VerticalSelectField } from "./VerticalSelectField.js";

// The unfinished acceptance together with the create action that started it,
// so a Retry after reopening completes the way the user originally chose.
export interface UnfinishedQuickAcceptance extends UnfinishedPendingDocShareAcceptance {
  action: PendingAction;
}

interface Props {
  pendingDocShareId: string;
  handleClose: () => void;
  handleCreateAndOpen: (projectId: string) => void;
  handleCreateOnly: (projectId: string, projectTitle: string) => void;
  // Kept by the caller across modal sessions, so reopening a share after a
  // partial failure resumes that project instead of creating another one.
  unfinishedAcceptance?: UnfinishedQuickAcceptance | null;
  onUnfinishedAcceptanceChange?: (
    unfinished: UnfinishedQuickAcceptance | null
  ) => void;
}

// Single-screen "Create project" confirmation for accepting a pending shared
// quote. Loads the authorised prefill (plus the category list the trade
// select needs and the project custom-field configs), derives the
// UI-independent draft, and never writes anything except via its own two
// create actions.
export const QuickAcceptProjectModal = ({
  pendingDocShareId,
  handleClose,
  handleCreateAndOpen,
  handleCreateOnly,
  unfinishedAcceptance,
  onUnfinishedAcceptanceChange,
}: Props) => {
  const client = useApolloClient();
  const { t } = useTranslate(["ProjectCreate", "Dashboard", "Global"]);

  const query = usePendingDocShareProjectPrefillQuery({
    client,
    variables: { id: pendingDocShareId },
    fetchPolicy: "no-cache",
  });
  const { categories, loading: categoriesLoading } = useCategories();
  // Loaded before the form initialises, so the custom-field keys exist from
  // the first render (as in the general project-creation form).
  const { customFieldConfig, loading: customFieldConfigLoading } =
    useCustomFieldConfig("Project");
  const loading =
    query.loading || categoriesLoading || customFieldConfigLoading;

  const result = query.data?.pendingDocShareProjectPrefill;
  const prefill =
    result?.__typename === "PendingDocShareProjectPrefillResult"
      ? result
      : undefined;

  // Owned here rather than by the form, so the partial-failure dialog can
  // replace the whole modal instead of opening inside its shell.
  const pendingActionRef = React.useRef<PendingAction | null>(
    unfinishedAcceptance?.action ?? null
  );
  const orchestrator = useAcceptPendingDocShareOrchestrator(pendingDocShareId, {
    unfinishedAcceptance,
    onUnfinishedAcceptanceChange: unfinished =>
      onUnfinishedAcceptanceChange?.(
        unfinished && {
          ...unfinished,
          action: pendingActionRef.current ?? "only",
        }
      ),
  });

  if (orchestrator.state.status === "partial-failure") {
    return (
      <AcceptPendingDocSharePartialFailureModal
        failedStep={orchestrator.state.failedStep}
        projectId={orchestrator.state.projectId}
        projectTitle={orchestrator.state.projectTitle}
        retrying={orchestrator.state.retrying}
        handleClose={handleClose}
        handleRetry={async () => {
          const result = await orchestrator.retryAccept();
          if (!result) return;
          if (pendingActionRef.current === "open") {
            handleCreateAndOpen(result.projectId);
          } else {
            handleCreateOnly(result.projectId, result.projectTitle);
          }
        }}
      />
    );
  }

  // Dismissing mid-flight would unmount the modal while the create and accept
  // mutations run, so a failed accept would never show its partial-failure
  // dialog.
  const dismiss =
    orchestrator.state.status === "submitting" ? undefined : handleClose;

  // One dialog from the first frame: title, width and footer stay put while
  // the content switches from spinner (or error) to the form, so the dialog
  // neither opens twice nor swaps its footer.
  return (
    <ModalDialog maxWidth="sm" handleClose={dismiss}>
      <ModalTitle
        title={t("Create project", { ns: "ProjectCreate" })}
        handleClose={dismiss}
      />
      {!loading && prefill ? (
        <QuickAcceptProjectModalForm
          prefill={prefill}
          categories={categories}
          customFieldConfigs={customFieldConfig.filter(
            config => config.showInCreateForm
          )}
          orchestrator={orchestrator}
          pendingActionRef={pendingActionRef}
          handleClose={handleClose}
          handleCreateAndOpen={handleCreateAndOpen}
          handleCreateOnly={handleCreateOnly}
        />
      ) : (
        <>
          <ModalContent isLoading={loading} contentProps={contentProps}>
            <ErrorMessage
              message={t("The shared document could not be loaded", {
                ns: "Dashboard",
              })}
            />
          </ModalContent>
          <QuickAcceptFooter
            disabled
            isSubmitting={false}
            pendingAction={null}
            onCancel={handleClose}
            onCreateOnly={() => {}}
            onCreateAndOpen={() => {}}
          />
        </>
      )}
    </ModalDialog>
  );
};

type PendingAction = "open" | "only";

// Every state renders its content at this minimum height, so the step from
// spinner to form is a single, small one.
const contentProps = { sx: { minHeight: 480 } };

function QuickAcceptFooter({
  disabled,
  isSubmitting,
  pendingAction,
  onCancel,
  onCreateOnly,
  onCreateAndOpen,
}: {
  disabled: boolean;
  isSubmitting: boolean;
  pendingAction: PendingAction | null;
  onCancel: () => void;
  onCreateOnly: () => void;
  onCreateAndOpen: () => void;
}) {
  const { t } = useTranslate(["ProjectCreate", "Global"]);

  return (
    <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: "divider" }}>
      <Button
        variant="text"
        onClick={onCancel}
        disabled={isSubmitting}
        sx={{ color: "text.secondary" }}
      >
        {t("Cancel", { ns: "Global" })}
      </Button>
      <Button
        variant="outlined"
        color="primary"
        disabled={disabled}
        loading={isSubmitting && pendingAction === "only"}
        onClick={onCreateOnly}
      >
        {t("Create only", { ns: "ProjectCreate" })}
      </Button>
      <Button
        variant="contained"
        color="primary"
        disabled={disabled}
        loading={isSubmitting && pendingAction === "open"}
        onClick={onCreateAndOpen}
      >
        {t("Create project and open", { ns: "ProjectCreate" })}
      </Button>
    </DialogActions>
  );
}

interface FormValues extends CustomFieldsFormValues {
  title: string;
  type: ProjectType | null;
  vertical: string | null;
  buildingAddress: AddressInput | null;
  // Advanced settings section
  furtherCategoryIds: string[];
  teamIds: string[];
  description: string;
  budgetAmount?: Money;
  earliestStart: Moment | null;
  deadline: Moment | null;
  urgent: boolean;
  initialStatus: DesiredProjectInitialStatus;
}

// The Advanced settings section's draft values. A shared quote carries none of
// them except the further categories; the team defaults exactly as in the
// general project-creation form, and that default counts as the draft value.
interface AdvancedDraft {
  furtherCategoryIds: string[];
  teamIds: string[];
  customFields: CustomFieldsFormValues["customFields"];
}

// One origin per section field (the custom-field block counts as one), for
// the collapsed hint's count.
function advancedFieldOrigins(
  values: FormValues,
  advancedDraft: AdvancedDraft
): FieldOrigin[] {
  return [
    deriveIdListOrigin(
      values.furtherCategoryIds,
      advancedDraft.furtherCategoryIds
    ),
    deriveIdListOrigin(values.teamIds, advancedDraft.teamIds),
    deriveFieldOrigin(values.description, ""),
    deriveMoneyOrigin(values.budgetAmount, undefined),
    deriveDateOrigin(values.earliestStart, null),
    deriveDateOrigin(values.deadline, null),
    deriveBooleanOrigin(values.urgent),
    deriveBooleanOrigin(values.initialStatus === "contracted"),
    deriveCustomFieldsOrigin(values.customFields),
  ];
}

// The first field a create action must point the user to, in visual order:
// the title, the client, then the Advanced settings fields top to bottom.
type FailingField =
  | { kind: "title" }
  | { kind: "client" }
  | { kind: "section"; name: string };

function firstFailingField(
  errors: FormikErrors<FormValues>,
  clientState: PendingDocShareProjectDraftClient,
  customFieldConfigs: CustomFieldConfigFragment[]
): FailingField | null {
  if (errors.title) return { kind: "title" };
  if (clientState.kind === "none") return { kind: "client" };
  for (const name of [
    "description",
    "budgetAmount",
    "earliestStart",
    "deadline",
  ] as const) {
    if (errors[name]) return { kind: "section", name };
  }
  // Custom-field errors are nested by group and key, as their values are.
  const customFieldErrors = errors.customFields as
    | Record<string, Record<string, unknown> | undefined>
    | undefined;
  const failingConfig = customFieldConfigs.find(
    config => customFieldErrors?.[config.group]?.[config.key]
  );
  return failingConfig
    ? {
        kind: "section",
        name: `customFields.${failingConfig.group}.${failingConfig.key}`,
      }
    : null;
}

function QuickAcceptProjectModalForm({
  prefill,
  categories,
  customFieldConfigs,
  orchestrator,
  pendingActionRef,
  handleClose,
  handleCreateAndOpen,
  handleCreateOnly,
}: {
  prefill: PendingDocShareProjectPrefill_ResultFragment;
  categories: ReturnType<typeof useCategories>["categories"];
  // Only the configs flagged for the create form.
  customFieldConfigs: CustomFieldConfigFragment[];
  orchestrator: ReturnType<typeof useAcceptPendingDocShareOrchestrator>;
  pendingActionRef: React.MutableRefObject<PendingAction | null>;
  handleClose: () => void;
  handleCreateAndOpen: (projectId: string) => void;
  handleCreateOnly: (projectId: string, projectTitle: string) => void;
}) {
  const { t } = useTranslate([
    "ProjectCreate",
    "TicketCreate",
    "ProjectTypes",
    "Global",
  ]);
  const { currentUser, hasOrganisationPermission } = useUserData();
  const viewerTeams = currentUser?.teams ?? [];
  // Creating the deferred contact needs `crm_record_update`, which the
  // dashboard box does not require: without it, an unmatched quote client is
  // never proposed, and the user picks an existing contact instead.
  const canCreateContact = hasOrganisationPermission("crm_record_update");

  // Derived once per mount: the prefill result never changes underneath an
  // already-open modal (`fetchPolicy: "no-cache"` runs the query exactly once).
  // The draft is also every field's origin for its provenance hint.
  const draft = React.useMemo(
    () => mapPendingDocShareProjectPrefillToDraft(prefill),
    [prefill]
  );
  // The trade select shows one root category; every other source-project
  // category is a further category, editable in the Advanced settings.
  const { vertical: draftVertical, furtherCategoryIds: draftFurtherIds } =
    React.useMemo(
      () => splitDraftCategories(draft.categoryIds, categories),
      [draft.categoryIds, categories]
    );

  const advancedDraft = React.useMemo((): AdvancedDraft => {
    const teamId = resolveCreationTeamId(viewerTeams);
    return {
      furtherCategoryIds: draftFurtherIds,
      teamIds: teamId ? [teamId] : [],
      customFields: getCustomFieldsInitialValues(customFieldConfigs, []),
    };
  }, [draftFurtherIds, viewerTeams, customFieldConfigs]);

  const { state, submit } = orchestrator;

  const [advancedExpanded, setAdvancedExpanded] = React.useState(false);
  // Errors are shown only once a create action was chosen; the client's error
  // is derived from it, so it clears the moment a client is set.
  const [submitAttempted, setSubmitAttempted] = React.useState(false);
  const titleInputRef = React.useRef<HTMLInputElement>(null);
  const clientFieldRef = React.useRef<HTMLDivElement>(null);

  const draftClient: PendingDocShareProjectDraftClient =
    draft.client.kind === "newContact" && !canCreateContact
      ? { kind: "none" }
      : draft.client;
  const contactCandidate = canCreateContact ? draft.contactCandidate : null;
  const [clientState, setClientState] =
    React.useState<PendingDocShareProjectDraftClient>(draftClient);

  // Once a failed project step left a created contact behind, that contact is
  // the client: the card, the select and its create modal show it instead of
  // the candidate, so saving the create modal cannot create it a second time.
  const createdContact = state.status === "error" ? state.createdContact : null;
  React.useEffect(() => {
    if (!createdContact) return;
    setClientState({
      kind: "matched",
      crmOrganisationId: createdContact.crmOrganisationId,
      title: createdContact.title,
    });
  }, [createdContact]);

  const initialValues: FormValues = {
    title: draft.title,
    type: draft.type,
    vertical: draftVertical,
    buildingAddress: draft.buildingAddress,
    furtherCategoryIds: advancedDraft.furtherCategoryIds,
    teamIds: advancedDraft.teamIds,
    description: "",
    budgetAmount: undefined,
    earliestStart: null,
    deadline: null,
    urgent: false,
    initialStatus: "opportunity",
    customFields: advancedDraft.customFields,
  };

  // The section's fields validate as in the general project-creation form:
  // none is required, and the dates are independent of each other.
  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .label(t("Project name", { ns: "ProjectCreate" }))
      .required(),
    description: Yup.string().label(t("Description", { ns: "TicketCreate" })),
    earliestStart: Yup.date()
      .label(t("Earliest start", { ns: "TicketCreate" }))
      .nullable(),
    deadline: Yup.date()
      .label(t("Deadline", { ns: "TicketCreate" }))
      .nullable(),
    customFields: getCustomFieldsValidationSchema(customFieldConfigs),
  });

  const isSubmitting = state.status === "submitting";
  const clientError =
    submitAttempted && clientState.kind === "none"
      ? t("Please select or create a client / contractee.", {
          ns: "ProjectCreate",
        })
      : undefined;

  const handleSubmit = async (values: FormValues) => {
    const action = pendingActionRef.current;
    if (!action) return;
    // `submitWith` never gets here without a client; kept as the last line of
    // defence against creating a project without one.
    if (clientState.kind === "none") return;

    const result = await submit({
      title: values.title,
      type: values.type,
      categoryIds: recombineDraftCategories(
        values.vertical,
        values.furtherCategoryIds
      ),
      buildingAddress: values.buildingAddress,
      client: clientState,
      description: values.description,
      budgetAmount: values.budgetAmount?.amount ?? null,
      earliestStart: values.earliestStart?.format("YYYY-MM-DD") || null,
      deadline: values.deadline?.format("YYYY-MM-DD") || null,
      urgent: values.urgent,
      teamIds: values.teamIds,
      initialStatus: values.initialStatus,
      customFieldValues: getCustomFieldsSubmitValues(
        customFieldConfigs,
        values.customFields
      ),
    });

    if (!result) return;

    if (action === "open") {
      handleCreateAndOpen(result.projectId);
    } else {
      // The caller decides how to react to success (refetch, toast, close);
      // this modal never closes itself on success, mirroring the
      // create-and-open path where navigation is what unmounts it.
      handleCreateOnly(result.projectId, result.projectTitle);
    }
  };

  // Only the further categories can come from the quote; every other field
  // of the section has no counterpart there and shows a hint once changed.
  const takenFromQuote = t("Taken from the quote", { ns: "ProjectCreate" });
  const notContained = t("Not contained in the quote", { ns: "ProjectCreate" });
  const notSpecified = t("Not specified in the quote", { ns: "ProjectCreate" });
  // The suggested title carries a client part exactly when the quote had a
  // client display name.
  const titleHasClientPart = draft.title !== draft.quoteSummary.title;
  const titleTakenText = titleHasClientPart
    ? t("Prefilled: client name – quote title", { ns: "ProjectCreate" })
    : t("Prefilled from quote title (no client in quote)", {
        ns: "ProjectCreate",
      });

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {formikProps => {
        // Both create actions stay clickable: choosing one validates, marks
        // every failing field and focuses the first, and only a valid form
        // runs a mutation.
        const submitWith = async (action: PendingAction) => {
          setSubmitAttempted(true);
          const errors = await formikProps.validateForm();
          formikProps.setTouched(
            setNestedObjectValues<FormikTouched<FormValues>>(
              formikProps.values,
              true
            ),
            false
          );
          const failing = firstFailingField(
            errors,
            clientState,
            customFieldConfigs
          );
          if (failing) {
            if (failing.kind === "title") {
              titleInputRef.current?.focus();
            } else if (failing.kind === "client") {
              clientFieldRef.current?.querySelector("input")?.focus();
            } else {
              setAdvancedExpanded(true);
              // The section's fields become focusable only once it expanded.
              createRecursiveTryFocusElementFunction(
                `[name="${failing.name}"]`
              )();
            }
            return;
          }
          pendingActionRef.current = action;
          await formikProps.submitForm();
        };

        return (
          <>
            <AgentDialogFormValues />
            <ModalContent contentProps={contentProps}>
              <Stack spacing={2} direction="column">
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {t(
                    "Review the transferred details. The quote will be attached to the new project as an incoming document.",
                    { ns: "ProjectCreate" }
                  )}
                </Typography>

                <SharedQuoteSummaryCard quoteSummary={draft.quoteSummary} />

                {state.status === "error" && (
                  <Alert severity="error">
                    <AlertTitle>
                      {state.failedStep === "contact"
                        ? t("The client could not be created", {
                            ns: "ProjectCreate",
                          })
                        : t("The project could not be created", {
                            ns: "ProjectCreate",
                          })}
                    </AlertTitle>
                    {state.detail}
                  </Alert>
                )}

                <Box>
                  <TextField
                    name="title"
                    label={t("Project name", { ns: "ProjectCreate" })}
                    required
                    inputRef={titleInputRef}
                  />
                  <FieldOriginHint
                    fieldLabel={t("Project name", { ns: "ProjectCreate" })}
                    origin={deriveFieldOrigin(
                      formikProps.values.title,
                      draft.title
                    )}
                    takenText={titleTakenText}
                    absentText={titleTakenText}
                    onReset={() =>
                      formikProps.setFieldValue("title", draft.title)
                    }
                    disabled={isSubmitting}
                  />
                </Box>

                <Box>
                  <Box ref={clientFieldRef}>
                    <ClientField
                      clientState={clientState}
                      setClientState={setClientState}
                      draft={draft}
                      siteAddressLabel={
                        formikProps.values.buildingAddress
                          ? getAddressLabel(formikProps.values.buildingAddress)
                          : null
                      }
                      error={clientError}
                      canCreateContact={canCreateContact}
                      contactCreated={createdContact !== null}
                      disabled={isSubmitting}
                    />
                  </Box>
                  <FieldOriginHint
                    fieldLabel={t("Client / contractee", {
                      ns: "ProjectCreate",
                    })}
                    origin={deriveClientOrigin(
                      clientState,
                      draftClient,
                      contactCandidate
                    )}
                    takenText={takenFromQuote}
                    absentText={
                      draft.client.kind === "newContact"
                        ? t(
                            "The quote's client is not in your contacts yet, and you may not create contacts – please choose an existing contact.",
                            { ns: "ProjectCreate" }
                          )
                        : t(
                            "The quote contains no client – please choose or create a contact.",
                            { ns: "ProjectCreate" }
                          )
                    }
                    absentIcon="info"
                    trailing={
                      <ClientResolutionHint
                        clientState={clientState}
                        draftClient={draftClient}
                        contactCandidate={contactCandidate}
                        onChooseExisting={() => setClientState(draftClient)}
                        onChooseNew={() => {
                          if (contactCandidate) {
                            setClientState(contactCandidate);
                          }
                        }}
                        disabled={isSubmitting}
                      />
                    }
                    onReset={() => setClientState(draftClient)}
                    disabled={isSubmitting}
                  />
                </Box>

                <Box>
                  <AddressField
                    name="buildingAddress"
                    label={t("Property address", { ns: "ProjectCreate" })}
                  />
                  <FieldOriginHint
                    fieldLabel={t("Property address", { ns: "ProjectCreate" })}
                    origin={deriveAddressOrigin(
                      formikProps.values.buildingAddress,
                      draft.buildingAddress
                    )}
                    takenText={t(
                      "Project location from the quote – will be used as the location",
                      { ns: "ProjectCreate" }
                    )}
                    absentText={notContained}
                    onReset={() =>
                      formikProps.setFieldValue(
                        "buildingAddress",
                        draft.buildingAddress
                      )
                    }
                    disabled={isSubmitting}
                  />
                </Box>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <VerticalSelectField
                      name="vertical"
                      label={t("Trade", { ns: "ProjectCreate" })}
                      disabled={isSubmitting}
                    />
                    <FieldOriginHint
                      fieldLabel={t("Trade", { ns: "ProjectCreate" })}
                      origin={deriveFieldOrigin(
                        formikProps.values.vertical,
                        draftVertical
                      )}
                      takenText={takenFromQuote}
                      absentText={notSpecified}
                      onReset={() =>
                        formikProps.setFieldValue("vertical", draftVertical)
                      }
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <ProjectTypeField
                      formikProps={formikProps}
                      allowedProjectTypes={draft.allowedProjectTypes}
                    />
                    <FieldOriginHint
                      fieldLabel={t("Project Type", { ns: "ProjectTypes" })}
                      origin={deriveFieldOrigin(
                        formikProps.values.type,
                        draft.type
                      )}
                      takenText={takenFromQuote}
                      absentText={notSpecified}
                      onReset={() =>
                        formikProps.setFieldValue("type", draft.type)
                      }
                      disabled={isSubmitting}
                    />
                  </Grid>
                </Grid>

                <AdvancedSettingsSection
                  formikProps={formikProps}
                  advancedDraft={advancedDraft}
                  viewerTeams={viewerTeams}
                  isCraftsmanOrganisation={
                    currentUser?.organisation.isCraftsmanOrganisation ?? false
                  }
                  customFieldConfigs={customFieldConfigs}
                  expanded={advancedExpanded}
                  setExpanded={setAdvancedExpanded}
                  disabled={isSubmitting}
                />
              </Stack>
            </ModalContent>
            <QuickAcceptFooter
              disabled={isSubmitting}
              isSubmitting={isSubmitting}
              pendingAction={pendingActionRef.current}
              onCancel={handleClose}
              onCreateOnly={() => submitWith("only")}
              onCreateAndOpen={() => submitWith("open")}
            />
          </>
        );
      }}
    </Formik>
  );
}

// Every simple project-creation field the visible part does not show, with
// the same provenance hints. Collapsing only hides the fields: the values
// live in the one Formik form and survive.
function AdvancedSettingsSection({
  formikProps,
  advancedDraft,
  viewerTeams,
  isCraftsmanOrganisation,
  customFieldConfigs,
  expanded,
  setExpanded,
  disabled,
}: {
  formikProps: FormikProps<FormValues>;
  advancedDraft: AdvancedDraft;
  viewerTeams: { id: string; name: string }[];
  isCraftsmanOrganisation: boolean;
  customFieldConfigs: CustomFieldConfigFragment[];
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  disabled: boolean;
}) {
  const { t } = useTranslate([
    "ProjectCreate",
    "TicketCreate",
    "ProjectOverview",
  ]);
  const { values, setFieldValue } = formikProps;

  // Only the further categories can come from the quote; every other field
  // of the section has no counterpart there and shows a hint once changed.
  const takenFromQuote = t("Taken from the quote", { ns: "ProjectCreate" });
  const notContained = t("Not contained in the quote", { ns: "ProjectCreate" });

  const changedCount = advancedFieldOrigins(values, advancedDraft).filter(
    origin => origin === "changed"
  ).length;

  return (
    <CollapseSection
      id="quick-accept-advanced-settings"
      title={t("Advanced settings", { ns: "ProjectCreate" })}
      titleVariant="subtitle1"
      isInitiallyExpanded={false}
      expanded={expanded}
      setExpanded={setExpanded}
      // `CollapseSection` shows the hint only while collapsed.
      CollapsedHint={
        changedCount > 0 ? (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {t("{count} fields manually changed", {
              ns: "ProjectCreate",
              count: changedCount,
            })}
          </Typography>
        ) : undefined
      }
    >
      <Stack spacing={2} direction="column" sx={{ pt: 1 }}>
        <Box>
          <CategoriesInput
            alwaysShowLabel
            label={t("Further categories", { ns: "ProjectCreate" })}
            categoryIds={values.furtherCategoryIds}
            disabled={disabled}
            onChange={async categories => {
              await setFieldValue(
                "furtherCategoryIds",
                categories.map(category => category.id)
              );
            }}
            onClear={
              values.furtherCategoryIds.length > 0
                ? async () => {
                    await setFieldValue("furtherCategoryIds", []);
                  }
                : undefined
            }
          />
          <FieldOriginHint
            fieldLabel={t("Further categories", { ns: "ProjectCreate" })}
            origin={deriveIdListOrigin(
              values.furtherCategoryIds,
              advancedDraft.furtherCategoryIds
            )}
            takenText={takenFromQuote}
            absentText={notContained}
            onReset={() =>
              setFieldValue(
                "furtherCategoryIds",
                advancedDraft.furtherCategoryIds
              )
            }
            disabled={disabled}
          />
        </Box>

        <RestrictedByCapabilityWithDebug capability="teams">
          {viewerTeams.length > 1 && (
            <Box>
              <Autocomplete
                multiple
                size="small"
                options={viewerTeams}
                value={viewerTeams.filter(team =>
                  values.teamIds.includes(team.id)
                )}
                getOptionLabel={option => option.name}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                disabled={disabled}
                onChange={(_, selected) => {
                  setFieldValue(
                    "teamIds",
                    selected.map(team => team.id)
                  );
                }}
                renderInput={params => (
                  <MuiTextField
                    {...params}
                    label={t("Teams", { ns: "ProjectOverview" })}
                  />
                )}
              />
              <FieldOriginHint
                fieldLabel={t("Teams", { ns: "ProjectOverview" })}
                origin={deriveIdListOrigin(
                  values.teamIds,
                  advancedDraft.teamIds
                )}
                onReset={() => setFieldValue("teamIds", advancedDraft.teamIds)}
                disabled={disabled}
              />
            </Box>
          )}
        </RestrictedByCapabilityWithDebug>

        <Box>
          <TextField
            name="description"
            label={t("Description", { ns: "TicketCreate" })}
            multiline
            disabled={disabled}
          />
          <FieldOriginHint
            fieldLabel={t("Description", { ns: "TicketCreate" })}
            origin={deriveFieldOrigin(values.description, "")}
            onReset={() => setFieldValue("description", "")}
            disabled={disabled}
          />
        </Box>

        <Box>
          <FormattedMoneyField
            name="budgetAmount"
            label={t("Budget", { ns: "TicketCreate" })}
            type="money"
            disabled={disabled}
          />
          <FieldOriginHint
            fieldLabel={t("Budget", { ns: "TicketCreate" })}
            origin={deriveMoneyOrigin(values.budgetAmount, undefined)}
            onReset={() => setFieldValue("budgetAmount", undefined)}
            disabled={disabled}
          />
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <DatePickerField
              label={t("Earliest start", { ns: "TicketCreate" })}
              name="earliestStart"
              disabled={disabled}
            />
            <FieldOriginHint
              fieldLabel={t("Earliest start", { ns: "TicketCreate" })}
              origin={deriveDateOrigin(values.earliestStart, null)}
              onReset={() => setFieldValue("earliestStart", null)}
              disabled={disabled}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <DatePickerField
              label={t("Deadline", { ns: "TicketCreate" })}
              name="deadline"
              disabled={disabled}
            />
            <FieldOriginHint
              fieldLabel={t("Deadline", { ns: "TicketCreate" })}
              origin={deriveDateOrigin(values.deadline, null)}
              onReset={() => setFieldValue("deadline", null)}
              disabled={disabled}
            />
          </Grid>
        </Grid>

        <Stack direction="column">
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: "center", flexWrap: "wrap" }}
          >
            <CheckboxField
              label={t("Urgent", { ns: "TicketCreate" })}
              name="urgent"
              disabled={disabled}
            />
            <FieldOriginHint
              fieldLabel={t("Urgent", { ns: "TicketCreate" })}
              variant="marker"
              origin={deriveBooleanOrigin(values.urgent)}
              onReset={() => setFieldValue("urgent", false)}
              disabled={disabled}
            />
          </Stack>
          {isCraftsmanOrganisation && (
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: "center", flexWrap: "wrap" }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.initialStatus === "contracted"}
                    disabled={disabled}
                    onChange={e => {
                      setFieldValue(
                        "initialStatus",
                        e.currentTarget.checked ? "contracted" : "opportunity"
                      );
                    }}
                  />
                }
                label={t("Contract signed/agreed offline", {
                  ns: "ProjectCreate",
                })}
              />
              <FieldOriginHint
                fieldLabel={t("Contract signed/agreed offline", {
                  ns: "ProjectCreate",
                })}
                variant="marker"
                origin={deriveBooleanOrigin(
                  values.initialStatus === "contracted"
                )}
                onReset={() => setFieldValue("initialStatus", "opportunity")}
                disabled={disabled}
              />
            </Stack>
          )}
        </Stack>

        {customFieldConfigs.length > 0 && (
          <Box>
            <CustomFieldsArray
              values={values}
              isSubmitting={disabled}
              setFieldValue={setFieldValue}
              customFieldConfigs={customFieldConfigs}
              objectType="Project"
              useAutoSave={false}
            />
            <FieldOriginHint
              fieldLabel={t("Custom fields", { ns: "ProjectCreate" })}
              origin={deriveCustomFieldsOrigin(values.customFields)}
              onReset={() =>
                setFieldValue("customFields", advancedDraft.customFields)
              }
              disabled={disabled}
            />
          </Box>
        )}
      </Stack>
    </CollapseSection>
  );
}

function ProjectTypeField({
  formikProps,
  allowedProjectTypes,
}: {
  formikProps: FormikProps<FormValues>;
  allowedProjectTypes: ProjectType[];
}) {
  const { t } = useTranslate(["ProjectTypes"]);
  const { projectTypeOptions, projectTypeLabels } = useProjectTypes();

  return (
    <SelectField
      name="type"
      fullWidth
      options={[
        {
          value: "" as unknown as ProjectType,
          label: t("No project type", { ns: "ProjectTypes" }),
        },
        ...(allowedProjectTypes.length > 1
          ? projectTypeOptions.filter(option =>
              allowedProjectTypes.includes(option.value)
            )
          : projectTypeOptions),
      ]}
      label={t("Project Type", { ns: "ProjectTypes" })}
      onChange={value => {
        formikProps.setFieldValue(
          "type",
          value === ("" as unknown) ? null : value
        );
      }}
      renderValue={value =>
        value
          ? projectTypeLabels[value as ProjectType]
          : t("No project type", { ns: "ProjectTypes" })
      }
    />
  );
}

// A set client is shown as a card (CRM contact or the contact created on
// submit); the shared `CrmCompanySelect` with its search and create modal is
// visible without a client and while the user is changing a set one. The
// select stays mounted behind the card, so its create modal (opened from the
// "New contact" entry or by Enter on typed text) survives the switch back to
// the card. The "+" is the row's own, next to card and select alike, so it
// does not vanish on the blur its click causes. Its create modal starts from
// what the user is looking at: the text typed into the select (as Enter
// does), the quote's contact candidate while the quote's client is set or
// none is (so after the CRM mutation rejected it, e.g. the sharer's email,
// the user fixes the contact instead of retyping it), and an empty form
// while a contact the user picked is set. The resolution itself is explained
// in the field's provenance hint.
function ClientField({
  clientState,
  setClientState,
  draft,
  siteAddressLabel,
  error,
  canCreateContact,
  contactCreated,
  disabled,
}: {
  clientState: PendingDocShareProjectDraftClient;
  setClientState: (client: PendingDocShareProjectDraftClient) => void;
  draft: PendingDocShareProjectDraft;
  // The formatted site address shown with a contact created on submit.
  siteAddressLabel: string | null;
  // Set after a create action without a client; only the select can show it.
  error: string | undefined;
  canCreateContact: boolean;
  // The candidate already exists as a contact; prefilling it again would
  // invite a duplicate.
  contactCreated: boolean;
  disabled: boolean;
}) {
  const { t } = useTranslate(["ProjectCreate", "CrmOrganisations"]);
  const { currentUser } = useUserData();
  const projectClientFilters =
    currentUser?.organisation?.configurations?.projectClientFilters;

  // The user clicked the card to change the client: the select takes the
  // card's place until a contact is chosen or the select is left.
  const [selecting, setSelecting] = React.useState(false);
  const showSelect = clientState.kind === "none" || selecting;
  const selectRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (selecting) selectRef.current?.querySelector("input")?.focus();
  }, [selecting]);

  const createProcessRef = React.useRef<
    ModalOpenProcessRef<
      Omit<React.ComponentProps<typeof CrmCreateModal>, "handleClose"> & {
        handleClose?(): void;
      }
    >
  >();

  const createModalPrefill =
    draft.contactCandidate && !contactCreated
      ? newContactToCrmCreateFormPrefill(draft.contactCandidate)
      : undefined;

  // The quote's client is set: a match the draft made, or the candidate.
  const isDraftClientSet =
    clientState.kind === "newContact" ||
    (clientState.kind === "matched" &&
      draft.client.kind === "matched" &&
      draft.client.crmOrganisationId === clientState.crmOrganisationId);

  // The text typed into the select. Clicking "+" blurs the select, which ends
  // the selection and hides it, so the text is captured on mousedown, before
  // that blur, and used by the click that follows.
  const [typedText, setTypedText] = React.useState("");
  const typedFirstnameRef = React.useRef("");
  const openCreateFromPlus = () => {
    const firstname = typedFirstnameRef.current;
    typedFirstnameRef.current = "";
    createProcessRef.current?.open({
      prefill: firstname
        ? { firstname }
        : clientState.kind === "matched" && !isDraftClientSet
          ? undefined
          : createModalPrefill,
    });
  };

  const joinDetails = (parts: Array<string | null | undefined>) =>
    parts.filter(Boolean).join(" · ") || null;

  const selectCrmCompany = (crmCompany: CrmCompanySelectEntry | null) => {
    setSelecting(false);
    setClientState(
      crmCompany
        ? {
            kind: "matched",
            crmOrganisationId: crmCompany.id,
            title: crmCompany.title,
          }
        : { kind: "none" }
    );
  };

  const card = (() => {
    if (clientState.kind === "matched") {
      // The quote's email and the creation year describe the draft's match
      // only; a contact the user picked has no such data in the prefill.
      const isDraftMatch =
        draft.client.kind === "matched" &&
        draft.client.crmOrganisationId === clientState.crmOrganisationId;
      const createdAt = draft.matchedContactDetails?.createdAt;
      return (
        <ClientCard
          name={clientState.title}
          details={
            isDraftMatch
              ? joinDetails([
                  draft.contactCandidate?.email,
                  createdAt
                    ? t("Customer since {year}", {
                        ns: "ProjectCreate",
                        year: new Date(createdAt).getFullYear(),
                      })
                    : null,
                ])
              : null
          }
          badge="crm"
          onEdit={() => setSelecting(true)}
          onClear={() => setClientState({ kind: "none" })}
          disabled={disabled}
        />
      );
    }
    if (clientState.kind === "newContact") {
      return (
        <ClientCard
          name={contactCandidateDisplayName(clientState)}
          details={joinDetails([clientState.email, siteAddressLabel])}
          badge="new"
          onEdit={() => setSelecting(true)}
          onClear={() => setClientState({ kind: "none" })}
          disabled={disabled}
        />
      );
    }
    return null;
  })();

  return (
    <Stack direction="row" spacing={1}>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        {!showSelect && card}
        <Box ref={selectRef} sx={{ display: showSelect ? undefined : "none" }}>
          <CrmCompanySelect
            inputLabel={t("Client / contractee", { ns: "ProjectCreate" })}
            placeholder={t("Search contact or enter a new name…", {
              ns: "ProjectCreate",
            })}
            required
            crmCompanyId={null}
            onChange={selectCrmCompany}
            filterBusinessPartnerRolesAll={
              projectClientFilters?.businessPartnerRolesAll
            }
            filterBusinessPartnerRolesExclude={
              projectClientFilters?.businessPartnerRolesExclude
            }
            filterTagsExclude={projectClientFilters?.tagsExclude}
            canCreateNew={canCreateContact}
            showCreateButton={false}
            createNewLabel={t("New contact", { ns: "CrmOrganisations" })}
            createModalTitle={t("Create client", { ns: "CrmOrganisations" })}
            createModalPrefill={createModalPrefill}
            openOnFocus
            onClose={reason => {
              // An option was chosen: `onChange` follows, or the create
              // modal opens and `onCreateModalClose` ends the selection.
              if (reason !== "selectOption") setSelecting(false);
            }}
            onInputValueChange={setTypedText}
            onCreateModalClose={() => setSelecting(false)}
            error={error}
            disabled={disabled}
          />
        </Box>
      </Box>
      {!disabled && (
        <RestrictedByOrganisationPermissionWithDebug permission="crm_record_update">
          <IconButton
            color="primary"
            size="large"
            aria-label={t("Create client", { ns: "CrmOrganisations" })}
            onMouseDown={() => {
              typedFirstnameRef.current = showSelect ? typedText.trim() : "";
            }}
            onClick={openCreateFromPlus}
            sx={{ alignSelf: "center" }}
          >
            <AddIcon />
          </IconButton>
          <ModalOpenProcess
            ref={createProcessRef}
            Modal={CrmCreateModal}
            modalProps={{
              id: "quick-accept-create-crm-modal",
              title: t("Create client", { ns: "CrmOrganisations" }),
              // `prefill` is chosen per click in `openCreateFromPlus`.
              // Cancelling ends a selection started from the card, as the
              // select's own create modal does.
              handleClose: () => setSelecting(false),
              handleComplete: async (handleClose, organisation) => {
                selectCrmCompany(organisation);
                handleClose();
              },
            }}
          />
        </RestrictedByOrganisationPermissionWithDebug>
      )}
    </Stack>
  );
}
